import * as React from 'react';
import { IComparisonResult } from '../../../data/providers/reports/comparisonReportProvider';
import { Entity } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardHeader } from '../../shared/card/CardHeader';
import { Metric } from '../../shared/Metric';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode } from '../../shared/pdf/Pdf';
import { DataCell, DataColumn, LabelCell } from '../utils/Table';

interface IProps {
  result: IComparisonResult;
  colors: string[];
  pdfMode: EPdfMode;
  entity: Entity;
  comparisonEntityName: string;
}

export const ComparisonResult = (props: IProps): React.ReactElement => {
  return (
    <Notable
      pdf={
        props.pdfMode === EPdfMode.PREVIEW || props.pdfMode === EPdfMode.PRINT
      }
    >
      <Card
        style={{ marginTop: 'var(--grid-gap)' }}
        inverse={props.pdfMode === EPdfMode.PRINT}
      >
        <Column>
          <CardHeader surface={24}>
            <div>{props.result.metric.label}</div>
          </CardHeader>
          <Row style={{ height: '100%' }} shouldCollapse>
            <DataColumn>
              <LabelCell bgColor={props.colors[0]}>
                <div>{props.entity.name}</div>
                <div>
                  <div>({`${props.result.dataSetA.count} Tests`})</div>
                  <div>{props.result.dataSetA.timeframe}</div>
                </div>
              </LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.dataSetA.aggregate?.mean,
                    props.result.dataSetA.aggregate?.roundingPrecision
                  )}
                  units={props.result.dataSetA.aggregate?.units}
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell bgColor={props.colors[1]}>
                <div>{props.comparisonEntityName}</div>
                <div>
                  <div>({`${props.result.dataSetB.count} Tests`})</div>
                  <div>{props.result.dataSetB.timeframe}</div>
                </div>
              </LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.dataSetB.aggregate?.mean,
                    props.result.dataSetB.aggregate?.roundingPrecision
                  )}
                  units={props.result.dataSetB.aggregate?.units}
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell center>% Difference</LabelCell>
              <DataCell>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(props.result.percentDifference, 2) ?? '--'}
                  units='%'
                  isSecondary
                />
              </DataCell>
            </DataColumn>
            <DataColumn>
              <LabelCell center last>
                Total Difference
              </LabelCell>
              <DataCell lastInRow>
                <Metric
                  style={{ alignItems: 'center' }}
                  value={roundValue(
                    props.result.change,
                    props.result.dataSetA.aggregate?.roundingPrecision
                  )}
                  units={props.result.dataSetA.aggregate?.units}
                  isSecondary
                />
              </DataCell>
            </DataColumn>
          </Row>
        </Column>
      </Card>
    </Notable>
  );
};
